import { Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { Button, ErrorState, Skeleton } from '@shared/design-system/ui'
import { apiAuthHeaders, apiFetch, apiUrl } from '@shared/api/http'
import { MEMBER_PREVIEW_STUDENT_NAME } from '@features/member-access/model/preview-student'
import { MemberFileCard } from '@features/member-access/ui/MemberFileCard'
import { HlsVideoPlayer } from '@shared/ui/HlsVideoPlayer'
import { useSession } from '@shared/lib/auth/session-store'
import { LessonPlayerLayout } from '@features/member-access/ui/LessonPlayerLayout'
import { MemberAccessLayout } from '@features/member-access/ui/MemberAccessLayout'
import { MemberCourseOffer } from '@features/member-access/ui/MemberCourseOffer'
import { MemberCourseTree } from '@features/member-access/ui/MemberCourseTree'
import {
  crumb,
  documentEmpty,
  documentList,
  lessonBack,
  lessonBody,
  muted,
  notice,
  pageIntro,
  subtitle,
  title,
} from '@features/member-access/ui/member-access.css'
import { MemberCatalogSection } from '@features/member-access/ui/MemberCatalog'
import { membersAreaDetailQuery, membersAreaListQuery } from '@features/members-area/model/members-area.queries'
import {
  previewCatalogAccess,
  previewShelfProductId,
  toMemberPreview,
  toMemberPreviewLesson,
} from '@features/members-area/model/members-preview'

async function openPreviewFile(productId: string, assetId: string) {
  const data = await apiFetch<{ downloadUrl: string }>(
    `/members-area/products/${productId}/assets/${assetId}/download`,
  )
  if (data.downloadUrl.startsWith('https://') || data.downloadUrl.startsWith('http://')) {
    window.open(data.downloadUrl, '_blank', 'noopener,noreferrer')
    return
  }
  const url = apiUrl(data.downloadUrl)
  const headers = new Headers()
  const auth = apiAuthHeaders(url)
  if (auth) {
    for (const [name, value] of Object.entries(auth)) headers.set(name, value)
  }
  const response = await fetch(url, { headers })
  if (!response.ok) {
    throw new Error('Não foi possível abrir o arquivo.')
  }
  const blobUrl = URL.createObjectURL(await response.blob())
  window.open(blobUrl, '_blank', 'noopener,noreferrer')
}

function PreviewBanner({ draft }: { draft: boolean }) {
  return (
    <div className={notice} data-testid="member-preview-banner">
      <span>
        {draft
          ? 'Prévia de rascunho. O aluno só vê esta página depois de publicar.'
          : 'Prévia de como o aluno vê. Nada é gravado.'}
      </span>
      <Link to="/area-membros">
        <Button size="sm" variant="secondary">
          Voltar ao painel
        </Button>
      </Link>
    </div>
  )
}

export function MemberPreviewPage({ productId }: { productId: string }) {
  const detailQuery = useQuery(membersAreaDetailQuery(productId))
  const listQuery = useQuery(membersAreaListQuery())
  const producerName = useSession((state) => state.user?.name?.trim() || 'Área de membros')

  if (detailQuery.isPending) {
    return (
      <MemberAccessLayout preview producerName={producerName}>
        <Skeleton height={280} radius="16px" />
      </MemberAccessLayout>
    )
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <MemberAccessLayout preview producerName={producerName}>
        <ErrorState
          title="Não foi possível abrir a prévia"
          description="Tente de novo a partir da área de membros."
          onRetry={() => void detailQuery.refetch()}
        />
      </MemberAccessLayout>
    )
  }

  const preview = toMemberPreview(detailQuery.data, producerName)
  if (preview.product.kind === 'files') {
    return (
      <MemberAccessLayout preview producerName={preview.producerName} previewStudentName={MEMBER_PREVIEW_STUDENT_NAME}>
        <PreviewBanner draft={preview.draft} />
        <header className={pageIntro}>
          <h1 className={title}>{preview.product.name}</h1>
          <p className={subtitle}>
            {preview.files.length} {preview.files.length === 1 ? 'arquivo' : 'arquivos'}
          </p>
        </header>
        {preview.files.length === 0 ? (
          <div className={documentEmpty}>
            <p>Nenhum arquivo nesta entrega ainda.</p>
          </div>
        ) : (
          <div className={documentList}>
            {preview.files.map((file) => (
              <MemberFileCard
                key={file.id}
                name={file.name}
                sizeBytes={file.sizeBytes}
                contentType={file.contentType}
                ready={file.ready}
                onOpen={
                  file.ready ? () => void openPreviewFile(productId, file.id) : undefined
                }
              />
            ))}
          </div>
        )}
      </MemberAccessLayout>
    )
  }

  const others = (listQuery.data ?? []).filter(
    (item) => item.status === 'PUBLISHED' && item.productId !== productId,
  )
  const items = [
    {
      productId,
      name: preview.product.name,
      coverImage: preview.product.coverImage,
      kind: preview.product.kind,
      offerUrl: null,
      continueLessonId: preview.product.continueLessonId,
      continueLessonTitle: preview.product.continueLessonTitle,
      fileCount: preview.product.fileCount,
      ...previewCatalogAccess(productId, productId),
    },
    ...others.map((item) => ({
      productId: item.productId,
      name: item.name,
      coverImage: item.coverImage,
      kind: item.kind,
      offerUrl: item.salesPageUrl || null,
      continueLessonId: null,
      continueLessonTitle: null,
      fileCount: item.fileCount,
      ...previewCatalogAccess(item.productId, productId),
    })),
  ].sort((a, b) => Number(a.accessStatus === 'LOCKED') - Number(b.accessStatus === 'LOCKED'))

  return (
    <MemberAccessLayout preview producerName={preview.producerName}>
      <PreviewBanner draft={preview.draft} />
      <header className={pageIntro}>
        <h1 className={title}>Meus conteudos</h1>
        <p className={subtitle}>Escolha um curso para ver as aulas.</p>
      </header>
      <MemberCatalogSection producerId="" previewProductId={productId} items={items} />
    </MemberAccessLayout>
  )
}

export function MemberPreviewCoursePage({ productId }: { productId: string }) {
  const navigate = useNavigate()
  const detailQuery = useQuery(membersAreaDetailQuery(productId))
  const producerName = useSession((state) => state.user?.name?.trim() || 'Área de membros')

  if (detailQuery.isPending) {
    return (
      <MemberAccessLayout preview producerName={producerName}>
        <Skeleton height={280} radius="16px" />
      </MemberAccessLayout>
    )
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <MemberAccessLayout preview producerName={producerName}>
        <ErrorState
          title="Não foi possível abrir a prévia"
          description="Tente de novo a partir da área de membros."
          onRetry={() => void detailQuery.refetch()}
        />
      </MemberAccessLayout>
    )
  }

  const preview = toMemberPreview(detailQuery.data, producerName)
  if (preview.product.kind === 'files') {
    return <MemberPreviewPage productId={productId} />
  }
  if (preview.product.continueLessonId) {
    void navigate({
      to: '/area-membros/preview/$productId/aulas/$lessonId',
      params: { productId, lessonId: preview.product.continueLessonId },
    })
    return null
  }
  void navigate({
    to: '/area-membros/preview/$productId',
    params: { productId: previewShelfProductId(productId) },
  })
  return null
}

export function MemberPreviewOfferPage({ productId }: { productId: string }) {
  const detailQuery = useQuery(membersAreaDetailQuery(productId))
  const producerName = useSession((state) => state.user?.name?.trim() || 'Área de membros')

  if (detailQuery.isPending) {
    return (
      <MemberAccessLayout preview producerName={producerName} previewBackProductId={previewShelfProductId(productId)}>
        <Skeleton height={280} radius="16px" />
      </MemberAccessLayout>
    )
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <MemberAccessLayout preview producerName={producerName} previewBackProductId={previewShelfProductId(productId)}>
        <ErrorState
          title="Não foi possível abrir a prévia"
          description="Tente de novo a partir da área de membros."
          onRetry={() => void detailQuery.refetch()}
        />
      </MemberAccessLayout>
    )
  }

  const preview = toMemberPreview(detailQuery.data, producerName)
  return (
    <MemberAccessLayout preview
      producerName={preview.producerName}
      previewBackProductId={previewShelfProductId(productId)}
    >
      <MemberCourseOffer
        offer={{
          name: preview.product.name,
          coverImage: preview.product.coverImage,
          description: detailQuery.data.description ?? '',
          modules: preview.modules.map((module) => ({
            id: module.id,
            title: module.title,
            lessonCount: module.lessons.length,
          })),
          priceLabel: detailQuery.data.priceLabel ?? null,
          checkoutUrl: detailQuery.data.checkoutUrl ?? null,
        }}
      />
    </MemberAccessLayout>
  )
}

export function MemberPreviewLessonPage({
  productId,
  lessonId,
}: {
  productId: string
  lessonId: string
}) {
  const navigate = useNavigate()
  const detailQuery = useQuery(membersAreaDetailQuery(productId))
  const producerName = useSession((state) => state.user?.name?.trim() || 'Área de membros')

  if (detailQuery.isPending) {
    return (
      <MemberAccessLayout preview producerName={producerName}>
        <Skeleton height={280} radius="16px" />
      </MemberAccessLayout>
    )
  }
  if (detailQuery.isError || !detailQuery.data) {
    void navigate({ to: '/area-membros/preview/$productId', params: { productId } })
    return null
  }

  const view = toMemberPreviewLesson(detailQuery.data, lessonId, producerName)
  const nextLessonId = view.nextLessonId
  const playbackUrl = view.lesson?.kind === 'video' ? view.lesson.playbackUrl : null
  if (!view.lesson) {
    void navigate({ to: '/area-membros/preview/$productId', params: { productId } })
    return null
  }

  return (
    <MemberAccessLayout preview variant="player" producerName={view.course.producerName}>
      <LessonPlayerLayout
        advanceKey={lessonId}
        title={view.lesson.title}
        back={
          <Link
            className={lessonBack}
            to="/area-membros/preview/$productId"
            params={{ productId: previewShelfProductId(productId) }}
            aria-label="Voltar ao catálogo"
            data-testid="lesson-back"
          >
            <ArrowLeft size={18} />
          </Link>
        }
        course={<span className={crumb}>{view.course.product.name}</span>}
        onAdvance={
          nextLessonId
            ? () =>
                void navigate({
                  to: '/area-membros/preview/$productId/aulas/$lessonId',
                  params: { productId, lessonId: nextLessonId },
                })
            : undefined
        }
        video={
          playbackUrl
            ? ({ onEnded }) => (
                <HlsVideoPlayer
                  flush
                  transport
                  src={apiUrl(playbackUrl)}
                  title={view.lesson?.title ?? ''}
                  requestHeaders={(url) => apiAuthHeaders(url)}
                  onEnded={onEnded}
                />
              )
            : undefined
        }
        previous={
          view.previousLessonId ? (
            <Link
              to="/area-membros/preview/$productId/aulas/$lessonId"
              params={{ productId, lessonId: view.previousLessonId }}
            >
              <Button variant="secondary">Aula anterior</Button>
            </Link>
          ) : null
        }
        next={
          view.nextLessonId ? (
            <Link
              to="/area-membros/preview/$productId/aulas/$lessonId"
              params={{ productId, lessonId: view.nextLessonId }}
            >
              <Button>Próxima aula</Button>
            </Link>
          ) : null
        }
        tree={
          <MemberCourseTree
            preview
            placement="rail"
            currentLessonId={lessonId}
            productId={productId}
            modules={view.course.modules}
          />
        }
      >
        {view.lesson.body ? (
          <p className={lessonBody}>{view.lesson.body}</p>
        ) : view.lesson.kind !== 'video' ? (
          <p className={muted}>Esta aula ainda não tem conteúdo.</p>
        ) : null}
        {view.lesson.assets.map((asset) => (
          <MemberFileCard
            key={asset.id}
            name={asset.name}
            sizeBytes={asset.sizeBytes}
            contentType={asset.contentType}
            onOpen={() => void openPreviewFile(productId, asset.id)}
          />
        ))}
      </LessonPlayerLayout>
    </MemberAccessLayout>
  )
}
